import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useDeleteAction } from '@/models/article';
import { ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { ActionAdditionalInfoInputs } from './ActionCard/ActionAdditionalInfo';
import { ActionBrandResponsibilityInput } from './ActionCard/ActionBrandResponsibility';
import { ActionCustomDescriptionInput } from './ActionCard/ActionCardHeader';
import { ActionContextInput } from './ActionCard/ActionContext';
import { ActionDefectPhotosInput } from './ActionCard/ActionDefectPhotos';
import { ActionDefectsInput } from './ActionCard/ActionDefects';
import { ActionPriceInput } from './ActionCard/ActionPriceInput';
import { ActionSparePartsMobileList } from './ActionCard/ActionSparePartsMobile';
import ActionName from './ActionName';
import PackActionName from './PackActionName';

type CreateEditActionDialogProps = {
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  mode: 'create' | 'edit';
};

export const CreateEditActionDialog = ({
  isOpen,
  setIsOpen,
  action,
  previousAction,
  mode,
}: CreateEditActionDialogProps) => {
  const { article, request } = useArticleContext();
  const { t } = useLingui();

  const title =
    mode === 'edit'
      ? t({ id: 'create-action-dialog.title.edit', message: 'Edit action' })
      : t({ id: 'create-action-dialog.title', message: 'Add an action' });

  const { mutate: deleteAction } = useDeleteAction({
    articleId: article.id,
    requestId: request.id,
  });

  return (
    <Dialog
      title={title}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        setIsOpen(newIsOpen);

        if (!newIsOpen && mode === 'create' && !!action) {
          deleteAction(action.id);
        }
      }}
    >
      {!!action && (
        <CreateEditActionForm
          onSave={() => setIsOpen(false)}
          action={action}
          previousAction={previousAction}
        />
      )}
    </Dialog>
  );
};

const CreateEditActionForm = ({
  onSave,
  action,
  previousAction,
}: {
  onSave: () => void;
  action: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();

  const actionType =
    action?.actionTypeOrganization ?? action?.packActionTypeOrganization ?? 'custom';

  const additionalInfoConfig =
    !!actionType && actionType !== 'custom' ? (actionType.additionalInfoConfig ?? []) : [];

  const showAdditionalInfo =
    !!additionalInfoConfig.length && view.services.actions.fields.additionalInfo.editable;
  const showDefects =
    view.services.actions.fields.associatedDefects.shown &&
    view.services.actions.fields.associatedDefects.editable;
  const showDefectPhotos =
    view.services.actions.fields.photo.shown && view.services.actions.fields.photo.editable;
  const showContext =
    view.services.actions.fields.context.shown && view.services.actions.fields.context.editable;
  const showBrandResponsibility =
    view.services.actions.fields.brandResponsibility.shown &&
    view.services.actions.fields.brandResponsibility.editable;
  const showDescriptionInput = view.services.actions.fields.description.editable;
  const showWorkshopPrice =
    requestView.workshopPrice.shown && view.services.actions.fields.workshopPrice.editable;
  const showOrganizationPrice =
    requestView.organizationPrice.shown && view.services.actions.fields.organizationPrice.editable;
  const showSpareParts =
    view.services.actions.fields.spareParts.shown &&
    (view.services.actions.fields.spareParts.canAddRemoveSparePart ||
      view.services.actions.fields.spareParts.quantity.editable ||
      view.services.actions.fields.spareParts.referenceNumber.editable);

  return (
    <>
      <main>
        <Stack gap="1rem">
          {actionType === 'custom' ? (
            showDescriptionInput ? (
              <ActionCustomDescriptionInput action={action} />
            ) : (
              <Paragraph>{action.customDescription}</Paragraph>
            )
          ) : 'actions' in actionType ? (
            <PackActionName packActionType={actionType} />
          ) : (
            <ActionName actionType={actionType} />
          )}
          {(showAdditionalInfo ||
            showDefects ||
            showDefectPhotos ||
            showContext ||
            showBrandResponsibility ||
            showWorkshopPrice ||
            showOrganizationPrice) && <hr />}
          {showAdditionalInfo && <ActionAdditionalInfoInputs action={action} />}
          {showDefects && <ActionDefectsInput action={action} />}
          {showDefectPhotos && <ActionDefectPhotosInput action={action} />}
          {showContext && <ActionContextInput action={action} />}
          {showBrandResponsibility && <ActionBrandResponsibilityInput action={action} />}
          {showSpareParts && (
            <ActionSparePartsMobileList action={action} previousAction={previousAction} />
          )}
          {showWorkshopPrice && <ActionPriceInput action={action} type="workshop" />}
          {showOrganizationPrice && <ActionPriceInput action={action} type="organization" />}
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button variant="primary" onPress={onSave}>
            <Trans id="create-action-dialog.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
